/**
 * La file de correction de l'enseignant (docs/12 §4).
 *
 * Les plus vieilles copies d'abord — à rebours du reste de l'application, qui
 * montre la nouveauté en premier. L'ordre vient du serveur et n'est pas rejoué
 * ici : ce qu'on corrige d'abord est ce qui attend depuis le plus longtemps, et
 * un tri côté client redeviendrait faux à la première page suivante.
 *
 * ⚠️ La file ne montre que les cours de l'appelant parce que le **serveur** l'a
 * bornée, pas parce que l'écran filtre. Cliquer sur une copie qui ne serait pas
 * la sienne aboutirait à un 403, et c'est la vraie garde (docs/07 §8).
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useGradingQueue, type GradingQueueItem } from "@/features/learning/api/useGrading";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function GradingQueuePage() {
  const { t } = useTranslation("learning");
  const queue = useGradingQueue();

  if (queue.isPending) {
    return <Skeleton className="mx-auto my-8 h-64 w-full max-w-4xl" label={t("grading.title")} />;
  }
  if (queue.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={queue.error} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("grading.title")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("grading.subtitle")}</p>
      </header>

      {queue.data.length === 0 ? (
        <Card title={t("grading.title")}>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("grading.empty")}</p>
        </Card>
      ) : (
        <ul className="space-y-3">
          {queue.data.map((item) => (
            <li key={item.attempt_id}>
              <QueueRow item={item} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function QueueRow({ item }: { item: GradingQueueItem }) {
  const { t } = useTranslation("learning");
  return (
    <Card title={item.exercise_title}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <p className="text-sm">
            <span className="font-medium">{item.student_name}</span>
            <span className="text-slate-500 dark:text-slate-400"> — {item.course_title}</span>
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t("grading.attemptNumber", { number: item.attempt_number })}
          </p>
          {/* Une copie bloquante retient l'élève sur la suite du cours : c'est
              elle qu'il faut lire d'abord, et l'écran ne peut le dire que parce
              que le serveur le lui apprend. */}
          {item.is_blocking ? (
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
              {t("grading.blocking")}
            </p>
          ) : null}
        </div>
        <Link
          to="/teach/grading/$attemptId"
          params={{ attemptId: item.attempt_id }}
          className="text-brand-600 text-sm hover:underline"
        >
          {t("grading.open")}
        </Link>
      </div>
    </Card>
  );
}
